import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ApplicationState } from "@idw-types/state/application-state";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { MessageTemplateDetailsComponent } from "./message-template-details-component/message-template-details-component";
import { CardContainer } from "@containers/common/card-container/card-container";
import { resources } from "@constants/hardcoded-strings";
import { getMessageTemplateDetailsReduxRequest } from "@redux-requests/message-template-details";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { MESSAGE_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES, MessageTemplateDetailsReferenceData } from "@constants/reference-data";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";
import { updateMessageTemplateDetailsReduxRequest } from "@redux-requests/message-template-details/update-message-template-details";
import { removeUnusedVariablesFromMessageTemplateLanguageFiles } from "@utils/message-template/message-template-util";

export const MessageTemplateDetailsContainer = () => {
    const dispatch = useDispatch();
    const { messageTemplateId } = useParams<{ messageTemplateId: string }>();

    const {
        isLoadingMessageTemplate,
        messageTemplate,
        session
    } = useSelector((state: ApplicationState) => ({
        isLoadingMessageTemplate: state.messageTemplateDetails.messageTemplate.isLoading,
        messageTemplate: state.messageTemplateDetails.messageTemplate.messageTemplate,
        session: state.session.session
    }));

    const referenceData = useReferenceDataSelector<MessageTemplateDetailsReferenceData>(MESSAGE_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES)

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadMessageTemplate, setShouldLoadMessageTemplate] = useState(true);


    const handleSave = async (messageTemplateState: FormattedMessageTemplate): Promise<FormattedMessageTemplate> => {
        const newLanguageFiles = removeUnusedVariablesFromMessageTemplateLanguageFiles(
            messageTemplateState.template,
            messageTemplateState.languages,
            messageTemplateState.type
        );
        const response = await updateMessageTemplateDetailsReduxRequest(dispatch)({
            ...messageTemplateState,
            languages: newLanguageFiles
        }, referenceData.axnCompanies);
        return response;
    };

    useEffect(() => {
        if (!referenceData.isLoading.messageTemplateDetails && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                MESSAGE_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.MESSAGE_TEMPLATE_DETAILS
            );
        }
    }, [referenceData.isLoading.messageTemplateDetails, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies
        );
    }, [
        referenceData.axnCompanies,
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingMessageTemplate && shouldLoadMessageTemplate) {
            setShouldLoadMessageTemplate(false);
            getMessageTemplateDetailsReduxRequest(dispatch)(messageTemplateId, referenceData.axnCompanies);
        }
    }, [isReferenceDataPresent, isLoadingMessageTemplate, shouldLoadMessageTemplate]);

    const isLoading = useMemo(() => {
        return isLoadingMessageTemplate || referenceData.isLoading.messageTemplateDetails;
    }, [isLoadingMessageTemplate, referenceData.isLoading.messageTemplateDetails]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            messageTemplate &&
            isReferenceDataPresent &&
            !shouldLoadMessageTemplate &&
            !shouldLoadReferenceData
        );
    }, [isLoading, messageTemplate, isReferenceDataPresent, shouldLoadMessageTemplate, shouldLoadReferenceData]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.messageTemplateDetails.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <MessageTemplateDetailsComponent
                        session={session}
                        initialMessageTemplate={messageTemplate}
                        axnCompanies={referenceData.axnCompanies}    
                        selfServiceVerificationWhiteLabeledEmails= {referenceData.selfServiceVerificationWhiteLabeledEmails}
                        handleSave={handleSave}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};